import { Vec3 } from '@/core/math/Vec3';
import { clamp } from '@/core/math/scalar';

/**
 * Conversions between planet-fixed cartesian coordinates, geodetic
 * latitude/longitude, cube-sphere face coordinates, and local tangent frames.
 *
 * Convention for the planet-fixed frame:
 *   +Y is the spin axis (north pole)
 *   +X passes through longitude 0, latitude 0 (the prime meridian)
 *   longitude increases eastward, which in this right-handed Y-up basis means
 *   rotating from +X toward -Z. Every angle in the project (spin, longitude of
 *   ascending node, argument of periapsis) uses that same right-handed sense
 *   about +Y, so "east" and "prograde spin" agree by construction.
 *
 * Bodies are treated as spheres, not ellipsoids. Terrain height is a
 * displacement along the radial direction. That simplification is deliberate and
 * consistent: gravity, altitude, the horizon and the cube-sphere all assume it,
 * so nothing in the simulation disagrees with anything else.
 */

export const CUBE_FACES = ['+X', '-X', '+Y', '-Y', '+Z', '-Z'] as const;
export type CubeFace = 0 | 1 | 2 | 3 | 4 | 5;

export interface LatLon {
  /** Radians, -PI/2 (south pole) to +PI/2 (north pole). */
  latitude: number;
  /** Radians, -PI (west) to +PI (east). */
  longitude: number;
}

export interface TangentBasis {
  east: Vec3;
  north: Vec3;
  up: Vec3;
}

/** Unit direction from geodetic latitude/longitude. */
export function directionFromLatLon(latitude: number, longitude: number, out = new Vec3()): Vec3 {
  const cosLat = Math.cos(latitude);
  out.x = cosLat * Math.cos(longitude);
  out.y = Math.sin(latitude);
  out.z = -cosLat * Math.sin(longitude);
  return out;
}

/** Geodetic latitude/longitude from a planet-fixed position. */
export function latLonFromPosition(position: Vec3): LatLon {
  const r = position.length();
  if (r === 0) return { latitude: 0, longitude: 0 };
  return {
    latitude: Math.asin(clamp(position.y / r, -1, 1)),
    longitude: Math.atan2(-position.z, position.x),
  };
}

/**
 * Local tangent basis (East-North-Up) at a planet-fixed position.
 * Falls back to a stable basis exactly at the poles.
 */
export function tangentBasis(position: Vec3): TangentBasis {
  const up = position.normalized();
  if (up.lengthSq() === 0) {
    return { east: new Vec3(1, 0, 0), north: new Vec3(0, 0, 1), up: new Vec3(0, 1, 0) };
  }
  const axis = new Vec3(0, 1, 0);
  let east = Vec3.crossVectors(axis, up);
  if (east.lengthSq() < 1e-18) {
    // At a pole the spin axis and the up vector are parallel; pick the prime
    // meridian direction so the basis stays continuous for anything approaching.
    east = Vec3.crossVectors(new Vec3(1, 0, 0), up);
    if (east.lengthSq() < 1e-18) east = new Vec3(0, 0, 1);
  }
  east.normalize();
  const north = Vec3.crossVectors(up, east).normalize();
  return { east, north, up };
}

/**
 * Great-circle (surface) distance between two directions on a sphere of the
 * given radius. Uses the numerically stable atan2 form rather than acos.
 */
export function surfaceDistance(dirA: Vec3, dirB: Vec3, radius: number): number {
  const a = dirA.normalized();
  const b = dirB.normalized();
  const cross = Vec3.crossVectors(a, b).length();
  const dot = a.dot(b);
  return radius * Math.atan2(cross, dot);
}

/** Identifies the cube face a unit direction belongs to. */
export function faceOfDirection(dir: Vec3): CubeFace {
  const ax = Math.abs(dir.x);
  const ay = Math.abs(dir.y);
  const az = Math.abs(dir.z);
  if (ax >= ay && ax >= az) return dir.x >= 0 ? 0 : 1;
  if (ay >= az) return dir.y >= 0 ? 2 : 3;
  return dir.z >= 0 ? 4 : 5;
}

/**
 * Maps a point on a cube face to a unit direction.
 * `u` and `v` are in [-1, 1] across the face.
 *
 * A plain normalise (rather than a spherified-cube polynomial) is used because
 * it inverts exactly, which the streaming system relies on to find the chunk
 * containing an arbitrary position.
 */
export function faceToDirection(face: CubeFace, u: number, v: number, out = new Vec3()): Vec3 {
  switch (face) {
    case 0: out.set(1, v, -u); break;
    case 1: out.set(-1, v, u); break;
    case 2: out.set(u, 1, -v); break;
    case 3: out.set(u, -1, v); break;
    case 4: out.set(u, v, 1); break;
    default: out.set(-u, v, -1); break;
  }
  return out.normalize();
}

export interface FaceCoord {
  face: CubeFace;
  u: number;
  v: number;
}

/** Exact inverse of `faceToDirection`. */
export function directionToFace(dir: Vec3): FaceCoord {
  const face = faceOfDirection(dir);
  const { x, y, z } = dir;
  switch (face) {
    case 0: return { face, u: -z / x, v: y / x };
    case 1: return { face, u: z / -x, v: y / -x };
    case 2: return { face, u: x / y, v: -z / y };
    case 3: return { face, u: x / -y, v: z / -y };
    case 4: return { face, u: x / z, v: y / z };
    default: return { face, u: -x / -z, v: y / -z };
  }
}

/**
 * Projects a direction onto a *specified* face, rather than onto whichever face
 * owns it.
 *
 * `directionToFace` picks the dominant axis, which is what chunk lookup wants.
 * LOD needs the other question answered: where does this direction fall on the
 * face I am already interested in? The answer only exists for directions in
 * that face's own hemisphere — beyond 90 degrees the projection diverges — so
 * this returns null instead of a nonsense coordinate.
 */
export function projectOntoFace(face: CubeFace, dir: Vec3): { u: number; v: number } | null {
  const { x, y, z } = dir;
  switch (face) {
    case 0: return x > 0 ? { u: -z / x, v: y / x } : null;
    case 1: return x < 0 ? { u: z / -x, v: y / -x } : null;
    case 2: return y > 0 ? { u: x / y, v: -z / y } : null;
    case 3: return y < 0 ? { u: x / -y, v: z / -y } : null;
    case 4: return z > 0 ? { u: x / z, v: y / z } : null;
    default: return z < 0 ? { u: -x / -z, v: y / -z } : null;
  }
}

/**
 * Rotation of the planet-fixed frame relative to the planet-centred inertial
 * frame at simulation time `t`.
 */
export function spinAngle(t: number, rotationPeriod: number, epochAngle = 0): number {
  if (rotationPeriod === 0) return epochAngle;
  return epochAngle + (2 * Math.PI * t) / rotationPeriod;
}

/**
 * Altitude of a position above the reference sphere (ignoring terrain).
 * Negative below the datum.
 */
export function altitudeAboveDatum(position: Vec3, radius: number): number {
  return position.length() - radius;
}

/**
 * Horizon distance for an observer at `altitude` above a sphere of `radius`,
 * used to size draw distance and terrain streaming radius.
 */
export function horizonDistance(altitude: number, radius: number): number {
  if (altitude <= 0) return 0;
  return Math.sqrt(altitude * (altitude + 2 * radius));
}
